import { Component, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { PurchaseOrder } from 'src/app/models/purchase-order';
import { Stock } from 'src/app/models/stock';
import { SupplierItem } from 'src/app/models/supplier-item.model';
import { InventoryService } from 'src/app/services/inventory.service';

@Component({
  selector: 'app-modal-line-item',
  template: `
    <ng-template #modalLineItem let-modal>
      <div class="modal-header">
        <h4 class="modal-title" id="modal-basic-title">Agregar Producto</h4>
        <button type="button" class="close btn btn-link" aria-label="Close" (click)="modal.dismiss('Cross click')">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body p-4">
        <form [formGroup]="formLineItem" (ngSubmit)="saveLineItem()">
          <!-- Stock Search -->
          <div class="form-group mt-4 position-relative">
            <label for="stock" class="form-control-label">Buscar Producto en Stock</label>
            <input
              type="text"
              class="form-control"
              formControlName="stockSearch"
              placeholder="Buscar producto..."
              (input)="onStockSearchInput($event)"
              [attr.autocomplete]="'off'"
              #searchInput>
            
            <!-- Stock Search Results -->
            <div class="search-results" *ngIf="showStockResults && filteredStock.length > 0">
              <div class="list-group">
                <button
                  type="button"
                  class="list-group-item list-group-item-action"
                  *ngFor="let stock of filteredStock"
                  (click)="selectStock(stock)">
                  <div class="d-flex justify-content-between">
                    <span>{{stock.name}}</span>
                    <!-- small>{{stock?.code}}</small -->
                  </div>
                  <small class="text-muted">Stock: {{stock.quantity}}</small>
                </button>
              </div>
            </div>
          </div>

          <!-- Supplier Search - Only shown after stock is selected -->
          <div class="form-group mt-4 position-relative" *ngIf="selectedStock">
            <label for="supplier" class="form-control-label">Seleccionar Proveedor</label>
            <select 
              class="form-control"
              formControlName="supplier_item_id"
              (change)="onSupplierSelect($event)">
              <option value="">Seleccionar proveedor</option>
              <option *ngFor="let item of supplierItems" [value]="item.id">
                 - {{item.unit_price | currency}}
              </option>
            </select>
          </div>

          <!-- Rest of the form -->
          <div class="form-group mt-4">
            <label for="quantity" class="form-control-label">Cantidad</label>
            <input 
              type="number" 
              class="form-control" 
              formControlName="quantity" 
              (input)="updateTotal()">
            <div *ngIf="ff['quantity'].touched && ff['quantity'].invalid">
              <div *ngIf="ff['quantity'].errors?.['required']" class="text-danger">Cantidad requerida</div>
            </div>
          </div>

          <div class="form-group mt-4">
            <label for="unit_price" class="form-control-label">Precio Unitario</label>
            <input 
              type="number" 
              class="form-control" 
              formControlName="unit_price" 
              (input)="updateTotal()"
              step="0.01">
          </div>

          <div class="form-group mt-4">
            <label for="total" class="form-control-label">Total</label>
            <input 
              type="number" 
              class="form-control" 
              formControlName="total" 
              readonly>
          </div>

          <div class="form-group mt-4">
            <label for="description" class="form-control-label">Notas</label>
            <textarea 
              class="form-control" 
              formControlName="description" 
              rows="3"></textarea>
          </div>

          <div class="form-group mt-4 text-end">
            <button type="button" class="btn btn-secondary me-2" (click)="dismiss()">Cancelar</button>
            <button type="submit" class="btn btn-zazz-dark" [disabled]="!formLineItem.valid">Guardar</button>
          </div>
        </form>
      </div>
    </ng-template>
  `,
  styles: [`
    .search-results {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: 1000;
      max-height: 200px;
      overflow-y: auto;
      background: white;
      border: 1px solid #ddd;
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
  `]
})
export class ModalLineItemComponent {
  @Input() purchase_order: PurchaseOrder | null = null;
  @Output() updateDismiss: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('modalLineItem') modalLineItem !: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  
  filteredStock: Stock[] = [];
  supplierItems: SupplierItem[] = [];
  showStockResults: boolean = false;
  selectedStock: Stock | null = null;
  showResults: boolean = false;

  private modalRef: NgbModalRef | undefined;
  formLineItem: FormGroup = new FormGroup({});

  query: any = {

  }

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private inventoryService: InventoryService
  ) {
    this.initForm();
    // this.setupSearchListener();
  }

  initForm() {
    this.formLineItem = this.fb.group({
      stockSearch: [''],
      stock_id: ['', Validators.required],
      supplier_item_id: ['', Validators.required],
      quantity: ['', [Validators.required, Validators.min(1)]],
      unit_price: ['', [Validators.required, Validators.min(0)]],
      total: [{value: 0, disabled: true}],
      description: ['']
    });
  }

  get ff() {
    return this.formLineItem.controls;
  }

  onStockSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (value && value.length >= 2) {
      this.searchStock(value);
    } else {
      this.showStockResults = false;
      this.filteredStock = [];
    }
  }

  searchStock(query: string) {
    this.inventoryService.getStock().subscribe({
      next: (response: any) => {
        console.log("STOCK RESPONSE ===> ", response);
        this.filteredStock = response.data.filter((stock: any) => 
          stock.name.toLowerCase().includes(query.toLowerCase())
        );
        this.showStockResults = true;
      },
      error: (error) => {
        this.toastr.error('Error searching stock');
      }
    });
  }

  selectStock(stock: Stock) {
    this.selectedStock = stock;
    this.formLineItem.patchValue({ stock_id: stock.id });
    this.showStockResults = false;
    this.loadSupplierItems(String(stock?.id));
  }

  loadSupplierItems(stockId: string) {
    this.inventoryService.getSupplierItemsByStock(stockId).subscribe({
      next: (items: any) => {
        console.log("SUPPLIER ITEMS ===> ", items);
        this.supplierItems = items.data;
        if (items.length === 0) {
          this.toastr.warning('No hay proveedores para este producto');
        }
      },
      error: (error) => {
        this.toastr.error('Error loading suppliers');
      }
    });
  }

  onSupplierSelect(event: Event) {
    const supplierId = (event.target as HTMLSelectElement).value;
    const supplierItem = this.supplierItems.find(item => item.id === Number(supplierId));
    if (supplierItem) {
      this.formLineItem.patchValue({
        unit_price: supplierItem.unit_price
      });
      this.updateTotal();
    }
  }

  updateTotal() {
    const quantity = this.formLineItem.get('quantity')?.value || 0;
    const unitPrice = this.formLineItem.get('unit_price')?.value || 0;
    this.formLineItem.patchValue({
      total: quantity * unitPrice
    }, { emitEvent: false });
  }

  saveLineItem() {
    console.log("purchase_order ===> ", this.purchase_order);
    if (this.formLineItem.valid) {
      const lineItem = {
        ...this.formLineItem.value,
        purchase_order_id: this.purchase_order?.id
      };

      this.inventoryService.addPurchaseOrderLineItem(this.purchase_order?.id, lineItem).subscribe({
        next: (response) => {
          this.toastr.success('Producto agregado correctamente');
          this.updateDismiss.emit(response);
          this.dismiss();
        },
        error: (error) => {
          this.toastr.error('Error al agregar la línea');
        }
      });
    }
  }

  openModal(modalContent: TemplateRef<any>) {
    this.modalRef = this.modalService.open(modalContent, { size: 'lg' });
  }

  dismiss() {
    if (this.modalRef) {
      this.updateDismiss.emit();
      this.modalRef.dismiss();
    }
  }
}
